import { useEffect, useRef } from 'react';
import useStore from '../store/useStore';
import { supabase } from '../lib/supabase';
import { generatePayDates, generateMonthlyDates, toDateStr } from '../utils/payDates';

const DEDUCTION_KEYS = [
  'federalTax', 'stateTax', 'socialSecurity', 'medicare',
  'retirement401k', 'hsa', 'healthInsurance', 'otherDeductions',
];

function getDeductions(p) {
  return DEDUCTION_KEYS.reduce((sum, k) => sum + (p[k] || 0), 0);
}

/**
 * Check if a transaction already exists in Supabase (not just local state).
 * This prevents double balance updates when both mobile and web run auto-postings.
 */
async function txExistsInDB(date, category, type) {
  if (!supabase) return false;
  const { count } = await supabase
    .from('transactions')
    .select('id', { count: 'exact', head: true })
    .eq('date', date)
    .eq('category', category)
    .eq('type', type);
  return (count || 0) > 0;
}

/**
 * Auto-posts paycheck snapshots/transactions and recurring postings on app open.
 * Runs once per session after data finishes loading.
 *
 * Balance updates are guarded by a DB check to prevent double-incrementing
 * when both mobile and web apps run auto-postings for the same user.
 */
export function useAutoPostings() {
  const loading = useStore((s) => s.loading);
  const autoPostingsEnabled = useStore((s) => s.userSettings?.autoPostingsEnabled ?? true);
  const ran = useRef(false);

  useEffect(() => {
    if (loading || ran.current || !autoPostingsEnabled) return;
    ran.current = true;

    const state = useStore.getState();
    const {
      paycheckSnapshots, transactions,
      addPaycheckSnapshot, addTransaction, updateAccount, updateCreditCard,
      upsertBalanceSheetMonth,
    } = state;
    const budgetConfig = state.userSettings?.budgetConfig || {};
    const payFrequency = budgetConfig.payFrequency || 'semi-monthly';
    const recurringPostings = budgetConfig.recurringPostings || [];
    const paycheckEndDate = budgetConfig.paycheckEndDate || null;

    (async () => {
      const todayStr = toDateStr(new Date());

      // ── 1. Auto-post paycheck snapshots + income transactions ──
      const sorted = [...paycheckSnapshots].sort((a, b) => (b.payDate || '').localeCompare(a.payDate || ''));
      const baseline = sorted.length > 0 ? sorted[0] : null;

      if (baseline && baseline.grossPay && sorted.length > 0) {
        const earliestPayDate = sorted[sorted.length - 1].payDate;
        const anchorDate = earliestPayDate;
        const year = new Date().getFullYear();
        const payDates = generatePayDates(year, payFrequency, anchorDate)
          .filter((d) => toDateStr(d) >= earliestPayDate)
          .filter((d) => toDateStr(d) >= todayStr) // Only today and forward — never recreate past dates
          .filter((d) => !paycheckEndDate || toDateStr(d) <= paycheckEndDate); // Stop at user-set last-paycheck date

        const existingPayDates = new Set(paycheckSnapshots.map((p) => p.payDate));
        const existingTxDates = new Set(
          transactions
            .filter((t) => t.category === 'Paycheck' && t.type === 'income')
            .map((t) => t.date),
        );

        const affectedMonths = new Set();

        for (const payDate of payDates) {
          const dateStr = toDateStr(payDate);

          if (!existingPayDates.has(dateStr)) {
            await addPaycheckSnapshot({
              payDate: dateStr,
              employer: baseline.employer || '',
              grossPay: baseline.grossPay || 0,
              ...Object.fromEntries(DEDUCTION_KEYS.map((k) => [k, baseline[k] || 0])),
            });
          }

          if (!existingTxDates.has(dateStr)) {
            // Double-check DB to prevent balance double-increment from mobile + web
            const alreadyInDB = await txExistsInDB(dateStr, 'Paycheck', 'income');
            const net = (baseline.grossPay || 0) - getDeductions(baseline);
            if (!alreadyInDB) {
              await addTransaction({
                date: dateStr,
                description: `Paycheck${baseline.employer ? ' — ' + baseline.employer : ''}`,
                amount: net,
                category: 'Paycheck',
                account: budgetConfig.paycheckAccountName || 'Checking',
                type: 'income',
              });
              // Safe to update balance — we confirmed no other app created this transaction
              if (budgetConfig.paycheckAccountId) {
                const store = useStore.getState();
                if (budgetConfig.paycheckAccountType === 'creditCard') {
                  const cc = store.creditCards.find((c) => c.id === budgetConfig.paycheckAccountId);
                  if (cc) await updateCreditCard(cc.id, { balance: (cc.balance || 0) + net, asOfDate: dateStr });
                } else {
                  const acct = store.accounts.find((a) => a.id === budgetConfig.paycheckAccountId);
                  if (acct) await updateAccount(acct.id, { balance: (acct.balance || 0) + net, asOfDate: dateStr });
                }
              }
            }
          }

          affectedMonths.add(dateStr.substring(0, 7));
        }

        // Sync 401k/HSA contributions to balance sheet
        const freshSnapshots = useStore.getState().paycheckSnapshots;
        for (const month of affectedMonths) {
          const monthPaychecks = freshSnapshots.filter((p) => p.payDate?.substring(0, 7) === month);
          const contrib401k = monthPaychecks.reduce((s, p) => s + (p.retirement401k || 0), 0);
          const contribHsa = monthPaychecks.reduce((s, p) => s + (p.hsa || 0), 0);
          if (contrib401k || contribHsa) {
            await upsertBalanceSheetMonth({ month, contrib401k, contribHsa });
          }
        }
      }

      // ── 2. Reconcile: sync all paycheck months' contributions ──
      if (paycheckSnapshots.length > 0) {
        const freshSnapshots = useStore.getState().paycheckSnapshots;
        const months = new Set(freshSnapshots.map((p) => p.payDate?.substring(0, 7)).filter(Boolean));
        for (const month of months) {
          const monthPaychecks = freshSnapshots.filter((p) => p.payDate?.substring(0, 7) === month);
          const contrib401k = monthPaychecks.reduce((s, p) => s + (p.retirement401k || 0), 0);
          const contribHsa = monthPaychecks.reduce((s, p) => s + (p.hsa || 0), 0);
          if (contrib401k || contribHsa) {
            await upsertBalanceSheetMonth({ month, contrib401k, contribHsa });
          }
        }
      }

      // ── 3. Auto-post recurring postings ──
      if (recurringPostings.length > 0) {
        const year = new Date().getFullYear();
        const freshTransactions = useStore.getState().transactions;

        for (const rule of recurringPostings) {
          if (!rule.enabled && rule.enabled !== undefined) continue;
          const dates = generateMonthlyDates(year, rule.dayOfMonth)
            .filter((d) => toDateStr(d) >= todayStr); // Only today and forward — never recreate past dates
          const existingDates = new Set(
            freshTransactions
              .filter((t) => t.description === rule.description && t.category === rule.category && t.type === rule.type)
              .map((t) => t.date),
          );
          for (const d of dates) {
            const dateStr = toDateStr(d);
            if (!existingDates.has(dateStr)) {
              // Double-check DB before creating + updating balance
              const alreadyInDB = await txExistsInDB(dateStr, rule.category, rule.type);
              if (!alreadyInDB) {
                await addTransaction({
                  date: dateStr,
                  description: rule.description,
                  amount: rule.amount,
                  category: rule.category,
                  account: rule.account || 'Checking',
                  type: rule.type,
                });
                if (rule.accountId) {
                  const balanceDelta = rule.type === 'income' ? rule.amount : -rule.amount;
                  const store = useStore.getState();
                  if (rule.accountType === 'creditCard') {
                    const cc = store.creditCards.find((c) => c.id === rule.accountId);
                    if (cc) await updateCreditCard(cc.id, { balance: (cc.balance || 0) + balanceDelta, asOfDate: dateStr });
                  } else {
                    const acct = store.accounts.find((a) => a.id === rule.accountId);
                    if (acct) await updateAccount(acct.id, { balance: (acct.balance || 0) + balanceDelta, asOfDate: dateStr });
                  }
                }
              }
            }
          }
        }
      }

      if (import.meta.env.DEV) console.log('[AutoPostings] Complete');
    })();
  }, [loading, autoPostingsEnabled]);
}
